import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getSupabaseAdmin } from "@/integrations/external-supabase/client.server";
import { resolveCurrentUserId } from "@/lib/articulos.server";
import { searchTerms } from "@/lib/search-text";
import { ordenarResultadosAgencias } from "@/lib/agencias-orden";

type Scope = { isStaff: boolean; agenciaIds: string[] } | null;

// Devuelve null si no hay sesión válida o el usuario no tiene acceso al panel.
// Roles y membresías se consultan a la vez: solo dependen del usuario.
async function getScope(userId: string | null): Promise<Scope> {
  if (!userId) return null;
  const sb = getSupabaseAdmin();
  const [{ data: roles }, { data: mems }] = await Promise.all([
    sb.from("user_roles").select("role").eq("user_id", userId),
    sb.from("agencia_miembros").select("agencia_id").eq("user_id", userId),
  ]);
  const rs = (roles ?? []).map((r: { role: string }) => r.role);
  const isStaff = rs.includes("admin") || rs.includes("customer_service") || rs.includes("agente");
  if (isStaff) return { isStaff: true, agenciaIds: [] as string[] };
  const agenciaIds = (mems ?? []).map((m: { agencia_id: string }) => m.agencia_id);
  if (agenciaIds.length === 0) return null;
  return { isStaff: false, agenciaIds };
}

const EMPTY = { agencias: [], agencias_total: 0, contactos: [], anuncios: [], demandas: [], operaciones: [] };
/** Agencias candidatas que se piden para ordenarlas por relevancia. */
const CANDIDATAS_AGENCIAS = 60;
const LIMIT = 8;

/**
 * Lanza las tres búsquedas a la vez. `agenciaIds` = null → sin acotar (staff);
 * con ids → solo lo de esas agencias.
 */
function buscar(terms: string[], agenciaIds: string[] | null) {
  const sb = getSupabaseAdmin();

  // ── Agencias ──
  // Se traen más candidatas de las que se muestran (las de más anuncios
  // primero) y luego se ordenan por relevancia del nombre: buscando "alfa"
  // deben salir antes las "Alfa …" que las que solo lo llevan en el email o la web.
  let agQ = sb
    .from("agencias")
    .select("id, nombre, slug, region, url_web, anuncios_count", { count: "exact" })
    .order("anuncios_count", { ascending: false })
    .limit(CANDIDATAS_AGENCIAS);
  for (const term of terms) agQ = agQ.ilike("busqueda", `%${term}%`);
  if (agenciaIds) agQ = agQ.in("id", agenciaIds);

  // ── Contactos ──
  let coQ = sb
    .from("contactos")
    .select("id, nombre, apellidos, email, telefono, ciudad, agencia_id")
    .limit(LIMIT);
  for (const term of terms) coQ = coQ.ilike("busqueda", `%${term}%`);
  if (agenciaIds) coQ = coQ.in("agencia_id", agenciaIds);

  // ── Anuncios ──
  let anQ = sb
    .from("anuncios")
    .select(
      "id, referencia, alias, direccion_completa, calle, numero, cp, slug, agencia_id, idealista_id",
    )
    .limit(LIMIT);
  for (const term of terms) anQ = anQ.ilike("busqueda", `%${term}%`);
  if (agenciaIds) anQ = anQ.in("agencia_id", agenciaIds);

  // ── Demandas y operaciones ──
  let deQ = sb.from("demandas").select("id, referencia, titulo, estado").limit(LIMIT);
  for (const term of terms) deQ = deQ.ilike("busqueda", `%${term}%`);
  if (agenciaIds) deQ = deQ.in("agencia_id", agenciaIds);

  let opQ = sb.from("transacciones").select("id, referencia, titulo, estado").limit(LIMIT);
  for (const term of terms) opQ = opQ.ilike("busqueda", `%${term}%`);
  if (agenciaIds) opQ = opQ.in("agencia_id", agenciaIds);

  return Promise.all([agQ, coQ, anQ, deQ, opQ]);
}

export const globalAdminSearch = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ q: z.string().trim().min(1).max(100) }).parse(d))
  .handler(async ({ data }) => {
    // Búsqueda sin acentos y por palabras sueltas sobre las columnas `busqueda`
    const terms = searchTerms(data.q);
    if (!terms.length) return EMPTY;

    const userId = await resolveCurrentUserId();
    // Sin sesión válida (token caducado/refrescando) → resultados vacíos en vez de error
    if (!userId) return EMPTY;

    // El caso habitual es el staff, que busca sin acotar: su búsqueda se lanza
    // a la vez que la comprobación de permisos y solo se usa si resulta ser
    // staff. Un miembro de agencia repite la búsqueda acotada a sus agencias
    // (las consultas tardan milisegundos; lo caro son los viajes en serie).
    const [scope, sinAcotar] = await Promise.all([getScope(userId), buscar(terms, null)]);
    if (!scope) return EMPTY;
    const [agRes, coRes, anRes, deRes, opRes] = scope.isStaff
      ? sinAcotar
      : await buscar(terms, scope.agenciaIds);

    const agencias = ordenarResultadosAgencias(agRes.data ?? [], data.q).slice(0, LIMIT);
    return {
      agencias,
      agencias_total: agRes.count ?? agencias.length,
      contactos: coRes.data ?? [],
      anuncios: anRes.data ?? [],
      demandas: deRes.data ?? [],
      operaciones: opRes.data ?? [],
    };
  });
